import { useState, useEffect, useMemo } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTags } from "@/features/admin/queries/useTags";
import { bulkAddTagsToFiles } from "@/services/api/files";
import { toast } from "sonner";
import type { FileItem } from "@/features/categories/types";

interface UseBulkAddTagsProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedFiles: FileItem[];
  onSuccess?: () => void;
}

export function useBulkAddTags({
  open,
  onOpenChange,
  selectedFiles,
  onSuccess,
}: UseBulkAddTagsProps) {
  const [selectedTagIds, setSelectedTagIds] = useState<Set<string | number>>(
    new Set()
  );
  const [searchQuery, setSearchQuery] = useState("");
  const queryClient = useQueryClient();

  const { data: availableTags = [], isLoading: tagsLoading } = useTags({
    enabled: open,
  });

  const selectedTags = useMemo(() => {
    return availableTags.filter((tag) => selectedTagIds.has(tag.id));
  }, [availableTags, selectedTagIds]);

  const filteredTags = useMemo(() => {
    return availableTags.filter(
      (tag) =>
        tag.isActive &&
        !selectedTagIds.has(tag.id) &&
        (tag.name?.toLowerCase().includes(searchQuery.toLowerCase()) ?? false)
    );
  }, [availableTags, selectedTagIds, searchQuery]);

  const mutation = useMutation({
    mutationFn: bulkAddTagsToFiles,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["gallery"] });
      toast.success("Tags added", {
        description: `Tags have been added to ${selectedFiles.length} file(s).`,
      });
      onOpenChange(false);
      setSelectedTagIds(new Set());
      onSuccess?.();
    },
    onError: (error: Error) => {
      toast.error("Failed to add tags", {
        description: error.message || "An error occurred while adding tags.",
      });
    },
  });

  useEffect(() => {
    if (!open) {
      setSelectedTagIds(new Set());
      setSearchQuery("");
    }
  }, [open]);

  const handleTagAdd = (tagId: string | number) => {
    setSelectedTagIds((prev) => {
      const next = new Set(prev);
      next.add(tagId);
      return next;
    });
    setSearchQuery("");
  };

  const handleTagRemove = (tagId: string | number) => {
    setSelectedTagIds((prev) => {
      const next = new Set(prev);
      next.delete(tagId);
      return next;
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (selectedTagIds.size === 0) {
      toast.error("No tags selected", {
        description: "Please select at least one tag to add.",
      });
      return;
    }

    mutation.mutate({
      fileIds: selectedFiles.map((f) => f.id),
      tagIds: Array.from(selectedTagIds).map((id) => Number(id)),
    });
  };

  return {
    selectedTagIds,
    selectedTags,
    filteredTags,
    tagsLoading,
    searchQuery,
    setSearchQuery,
    handleTagAdd,
    handleTagRemove,
    handleSubmit,
    isPending: mutation.isPending,
    hasAvailableTags: availableTags.length > 0,
  };
}
